import React, { Component, ReactNode } from "react"
import {
  QueryErrorResetBoundary,
  useQueryErrorResetBoundary,
} from "@tanstack/react-query"
import { Button, Box, Typography } from "@mui/material"
import { ErrorOutline, Refresh } from "@mui/icons-material"

interface QueryErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
  onError?: (error: Error, errorInfo: any) => void
}

interface QueryErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class QueryErrorBoundaryClass extends Component<
  QueryErrorBoundaryProps,
  QueryErrorBoundaryState
> {
  constructor(props: QueryErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): QueryErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: any) {
    console.error("Query Error Boundary caught an error:", error, errorInfo)
    this.props.onError?.(error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || <DefaultErrorFallback error={this.state.error} />
      )
    }

    return this.props.children
  }
}

const DefaultErrorFallback: React.FC<{ error: Error | null }> = ({ error }) => {
  const { reset } = useQueryErrorResetBoundary()

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "200px",
        p: 3,
        textAlign: "center",
      }}
    >
      <ErrorOutline sx={{ fontSize: 48, color: "error.main", mb: 2 }} />
      <Typography
        variant="h6"
        gutterBottom
      >
        Algo salió mal
      </Typography>
      <Typography
        variant="bodySmall"
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        {error?.message || "Ocurrió un error inesperado al cargar los datos."}
      </Typography>
      <Button
        variant="contained"
        startIcon={<Refresh />}
        onClick={() => {
          reset()
          window.location.reload()
        }}
        sx={{ mb: 2 }}
      >
        Reintentar
      </Button>
      <Button
        variant="outlined"
        onClick={() => (window.location.href = "/")}
      >
        Ir al inicio
      </Button>
    </Box>
  )
}

export const QueryErrorBoundary: React.FC<QueryErrorBoundaryProps> = ({
  children,
  ...props
}) => {
  return (
    <QueryErrorResetBoundary>
      <QueryErrorBoundaryClass {...props}>{children}</QueryErrorBoundaryClass>
    </QueryErrorResetBoundary>
  )
}

export default QueryErrorBoundary
